import { Button, Space } from 'antd';
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import styles from './ColumnFilterPanel.module.scss';

interface ColumnFilterPanelProps {
  children: ReactNode;
  onApply?: () => void;
  onReset?: () => void;
  showActions?: boolean;
}

/**
 * Renders table column filter UI with Apply/Reset always anchored inside
 * the same dropdown panel (footer stays visible while the filter body scrolls).
 */
export function ColumnFilterPanel({
  children,
  onApply,
  onReset,
  showActions = true,
}: ColumnFilterPanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null);

  const getPopupContainer = useCallback((triggerNode: HTMLElement) => {
    return (
      bodyRef.current ??
      (triggerNode.closest(`.${styles.panel}`) as HTMLElement | null) ??
      (triggerNode.closest('.ant-table-filter-dropdown') as HTMLElement | null) ??
      triggerNode.parentElement ??
      document.body
    );
  }, []);

  const enhancedChildren = Children.map(children, (child) => {
    if (!isValidElement(child) || typeof child.type === 'string') {
      return child;
    }

    // Compact, auto-apply filters use Ant's normal portal positioning.
    // Nesting their popup inside this panel caused oversized/clipped menus.
    if (!showActions) {
      return child;
    }

    const props = child.props as Record<string, unknown>;
    const next: Record<string, unknown> = {
      ...props,
      // Keep Select / DatePicker menus inside this panel so actions stay “with” the dropdown.
      getPopupContainer: (props.getPopupContainer as typeof getPopupContainer) ?? getPopupContainer,
    };

    // Select-only: don’t size the option list to a narrow column filter trigger.
    if ('mode' in props || 'optionFilterProp' in props || 'options' in props) {
      next.popupMatchSelectWidth = props.popupMatchSelectWidth ?? false;
    }

    return cloneElement(child as ReactElement<Record<string, unknown>>, next);
  });

  return (
    <div
      className={`${styles.panel} ${showActions ? '' : styles.compact}`}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={styles.body} ref={bodyRef}>
        {enhancedChildren}
      </div>
      {showActions ? (
        <div className={styles.actions}>
          <Space size={8}>
            <Button
              type="primary"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onApply?.();
              }}
            >
              Apply
            </Button>
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onReset?.();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ) : null}
    </div>
  );
}
